import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { platformApi } from '@/lib/api';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageLoading } from '@/components/ui/loading';
import { formatDate } from '@/lib/utils';
import { Building2, Users, FolderOpen, Activity, ShieldCheck } from 'lucide-react';

interface DashboardData {
  totalOrganizations: number;
  activeOrganizations: number;
  totalUsers: number;
  totalProjects: number;
  recentOrganizations: any[];
}

export default function PlatformDashboardPage() {
  const { user, support } = usePlatformAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'dashboard'],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: DashboardData }>('/platform/dashboard');
      return r.data;
    },
  });

  if (isLoading) return <PageLoading />;

  const stats = [
    { label: 'Organizations', value: data?.totalOrganizations ?? 0, icon: <Building2 size={20} /> },
    { label: 'Active Orgs', value: data?.activeOrganizations ?? 0, icon: <Activity size={20} /> },
    { label: 'Total Users', value: data?.totalUsers ?? 0, icon: <Users size={20} /> },
    { label: 'Total Projects', value: data?.totalProjects ?? 0, icon: <FolderOpen size={20} /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">Platform Dashboard</h1>
          <p className="text-sm text-secondary mt-1">Signed in as {user?.email}</p>
        </div>
        {support && (
          <Badge variant="warning">Support mode: {support.organizationName || support.organizationCode}</Badge>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(s => (
          <Card key={s.label}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-on-brand" style={{ backgroundColor: 'var(--brand-primary)' }}>
                {s.icon}
              </div>
              <div>
                <p className="text-sm text-secondary">{s.label}</p>
                <p className="text-2xl font-bold text-primary">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-primary">Recent Organizations</h2>
            <Link to="/super-admin/organizations"><Button variant="outline" size="sm">View All</Button></Link>
          </div>
          <div className="space-y-2">
            {(data?.recentOrganizations ?? []).map((o: any) => (
              <Link key={o.id} to={`/super-admin/organizations/${o.id}`} className="flex items-center justify-between rounded-lg border border-default px-4 py-3 hover:bg-surface-hover transition-colors">
                <div>
                  <p className="text-sm font-medium text-primary">{o.name}</p>
                  <p className="text-xs font-mono text-secondary">{o.organization_code}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={o.status === 'ACTIVE' ? 'approved' : 'rejected'}>{o.status}</Badge>
                  <span className="text-xs text-muted">{formatDate(o.created_at)}</span>
                </div>
              </Link>
            ))}
            {(data?.recentOrganizations ?? []).length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-muted gap-2">
                <ShieldCheck size={40} className="opacity-40" />
                <p>No organizations created yet</p>
                <Link to="/super-admin/organizations/new" className="mt-2"><Button size="sm" variant="outline">Create Organization</Button></Link>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
